/**
 * Contractor Materials Readiness (P8 polish + P9 bug fix)
 * ───────────────────────────────────────────────────────
 * Pure wrapper over the canonical getJobBomReconciliation() that collapses
 * the reconciled BOM into a single 5-state pill suitable for contractor-
 * facing job cards. NEVER exposes line items.
 *
 * P9 addition: when the job is in stage "Completed", the readiness pill
 * MUST short-circuit to "installed" — the BOM-vs-allocation join is
 * meaningless once the install is done, and showing "Pending" / "Partial"
 * on a finished job is the badge-semantics bug we're fixing.
 */

import type { Job } from "@/data/types";
import { getJobBomReconciliation } from "@/data/materialsReconciliation";

export type ContractorMaterialsReadiness =
  | "installed"
  | "ready"
  | "partial"
  | "pending"
  | "unknown";

export interface ContractorMaterialsView {
  readiness: ContractorMaterialsReadiness;
  label: string;
}

const LABEL: Record<ContractorMaterialsReadiness, string> = {
  installed: "Materials installed",
  ready: "Materials ready",
  partial: "Partially staged",
  pending: "Materials pending",
  unknown: "Materials TBD",
};

export function getContractorMaterialsReadiness(
  job: Job,
): ContractorMaterialsView {
  // P9: completed jobs always read as installed — readiness no longer applies.
  if (job.currentStage === "Completed") {
    return { readiness: "installed", label: LABEL.installed };
  }

  const recon = getJobBomReconciliation(job);
  if (recon.bom.source === "none" || recon.counts.total === 0) {
    return { readiness: "unknown", label: LABEL.unknown };
  }

  let stagedOrInstalled = 0;
  let allocatedOnly = 0;
  for (const r of recon.rows) {
    if (r.status === "off-spec") continue;
    const need = r.specQty || r.displayQty || 0;
    if (need <= 0) continue;
    const placed = r.quantityStaged + r.quantityInstalled;
    if (placed >= need) stagedOrInstalled++;
    else if (r.quantityAllocated >= need) allocatedOnly++;
  }

  const total = recon.counts.total;
  if (stagedOrInstalled === total) return { readiness: "ready", label: LABEL.ready };
  if (stagedOrInstalled === 0 && allocatedOnly === 0)
    return { readiness: "pending", label: LABEL.pending };
  return { readiness: "partial", label: LABEL.partial };
}
